import DropdownItem from "@/components/DropdownItem";
import Modal from "@/components/Modal";
import { useApp } from "@/store/app";
import { useReplicacheSetLists, useReplicacheSongs } from "@/store/replicache";
import type { SetList, SetListId, Song, SongId } from "@/utils/types";
import { without } from "lodash";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import { MdList } from "react-icons/md";

const isSongInSetList = (song: Song, setList: SetList) => {
  //console.debug("isSongInSetList", { song, setList });
  if (!setList || !song) return false;
  if (!setList.songIds) return false;
  return setList.songIds.includes(song.id);
};

export default function SetListMenuItem() {
  const { setListId, songId } = useParams() as {
    setListId: SetListId;
    songId: SongId;
  };
  const { songs } = useReplicacheSongs();
  const song = songs[songId];
  const { setLists } = useReplicacheSetLists();
  const isInSet = isSongInSetList(song, setLists[setListId]);

  const [setListId_, setSetListId_] = useState(setListId);
  const { setListModify } = useApp();
  const setListArray = Object.values(setLists);

  //console.debug("AddToSetListDialog", { isInSet, setListId });

  const modalId = "set-list-menu-modal";

  // set setListId_ to first set list, if there is one.
  useEffect(() => {
    //console.debug("SetListMenuItem", { setListId_, setListArray });
    if (setListId_) return;
    if (!setListArray.length) return;
    //console.debug("SetListMenuItem setting ID", { id: setListArray[0].id });
    setSetListId_(setListArray[0].id);
  }, [setListId_, setListArray]);

  const setListModify_ = (setList: SetList) => {
    const el = document.getElementById(modalId) as HTMLInputElement;
    //console.debug("checked?", el.checked);
    el.checked = false;
    //console.debug("checked now?", el.checked);
    setListModify(setList);
  };

  const handleRemove = () => {
    console.debug("SetListMenuItem handleRemove", {
      song,
      setListId_,
      isInSet,
    });
    const setList = setLists[setListId_];
    if (!setList) {
      throw new Error("No Set List for id:" + setListId_);
    }
    const newSetList = { ...setList };
    newSetList.songIds = without(setList.songIds, song.id);
    setListModify_(newSetList);
  };

  const handleAdd = () => {
    console.debug("SetListMenuItem handleAdd", { song, setListId_, isInSet });
    window.analyticsLogEvent("Add/Remove Song to Set List");
    const setList = setLists[setListId_];
    if (!setList) {
      throw new Error("No Set List for id:" + setListId_);
    }
    const newSetList = { ...setList };
    console.debug("SetListMenuItem", { newSetList, song });
    if (!newSetList.songIds) {
      newSetList.songIds = [song.id];
    } else {
      newSetList.songIds = [song.id, ...newSetList.songIds];
    }
    setListModify_(newSetList);
  };

  return (
    <>
      <DropdownItem>
        <label
          htmlFor={modalId}
          className="modal-button whitespace-nowrap w-full flex items-center"
        >
          <MdList className="text-2xl mr-2" />
          {isInSet ? "Remove from Set" : "Add to Set"}
        </label>
      </DropdownItem>

      <Modal id={modalId}>
        <h3 className="font-bold text-lg">
          {!!isInSet ? "Remove from" : "Add to"} Set
        </h3>
        <div className="divider" />

        <div>“{song?.title || "Song"}”</div>
        <select
          className="min-w-[120px] bg-[#484848] py-2 px-4 rounded my-4 outline-none border-[1px] border-[#ffffff10]"
          value={setListId_}
          onChange={(e) => setSetListId_(e.target.value)}
          disabled={setListId ? true : false}
        >
          {setListArray.map((setList) => (
            <option
              className="px-4 py-2"
              key={setList.id}
              value={setList.id}
              disabled={isSongInSetList(song, setList)}
            >
              {setList.title}
            </option>
          ))}
        </select>
        <div className="flex flex-end">
          <label htmlFor={modalId} className="btn btn-outline ml-auto mr-2">
            Cancel
          </label>
          <label htmlFor={modalId}>
            <button
              className="btn btn-primary"
              onClick={isInSet ? handleRemove : handleAdd}
              disabled={!setListId_}
            >
              {isInSet ? "Remove" : "Add"}
            </button>
          </label>
        </div>
      </Modal>
    </>
  );
}
